/**
 * Attachments on web: the chips above the composer's controls row (a small
 * preview, the file name and a remove button, popping in as they are added)
 * and the helpers the sent message's attachments share with them.
 */
import { useEffect, useState, type FC } from 'react';
import {
  AttachmentPrimitive,
  ComposerPrimitive,
  useAuiState,
} from '@assistant-ui/react';
import { useTranslation } from 'react-i18next';
import {
  File as FileIcon,
  FileCode,
  FileImage,
  FileSpreadsheet,
  FileText,
  X,
} from 'lucide-react';

function useFileSrc(file: File | undefined) {
  const [src, setSrc] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!file) {
      setSrc(undefined);
      return;
    }
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return src;
}

export function useAttachmentSrc() {
  const file = useAuiState((s) => {
    if (s.attachment.type !== 'image') return undefined;
    return s.attachment.file as File | undefined;
  });
  const contentSrc = useAuiState((s) => {
    if (s.attachment.type !== 'image') return undefined;
    const img = s.attachment.content?.find(
      (part: { type: string }) => part.type === 'image',
    ) as { image?: string } | undefined;
    return img?.image;
  });
  return useFileSrc(file) ?? contentSrc;
}

export function getFileExtension(name: string) {
  const parts = name.split('.');
  return parts.length > 1 ? parts.pop()!.toUpperCase() : '';
}

export function getFileTypeInfo(
  name: string,
  contentType?: string,
): { icon: typeof FileText; color: string; bg: string } {
  const ext = getFileExtension(name).toLowerCase();
  const mime = contentType ?? '';

  const neutral = {
    color: 'var(--text-secondary)',
    bg: 'rgba(0,0,0,0.06)',
  };

  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext))
    return { icon: FileImage, ...neutral };
  if (mime === 'application/pdf' || ext === 'pdf')
    return { icon: FileText, ...neutral };
  if (['doc', 'docx'].includes(ext) || mime.includes('wordprocessing'))
    return { icon: FileText, ...neutral };
  if (['xls', 'xlsx', 'csv'].includes(ext) || mime.includes('spreadsheet'))
    return { icon: FileSpreadsheet, ...neutral };
  if (['js', 'ts', 'tsx', 'jsx', 'py', 'rb', 'go', 'rs', 'json', 'html', 'css'].includes(ext))
    return { icon: FileCode, ...neutral };
  return { icon: FileIcon, ...neutral };
}

const RemoveChip: FC<{ name: string }> = ({ name }) => {
  const { t } = useTranslation('common');
  return (
    <AttachmentPrimitive.Remove asChild>
      <button
        type="button"
        className="tabbio-composer-chip-remove"
        aria-label={t('chat.composer.removeAttachment', { name })}
        title={t('chat.composer.removeAttachment', { name })}
      >
        <X size={11} strokeWidth={2.5} />
      </button>
    </AttachmentPrimitive.Remove>
  );
};

const ImageChip: FC = () => {
  const name = useAuiState((s) => s.attachment.name);
  const src = useAttachmentSrc();
  return (
    <AttachmentPrimitive.Root className="tabbio-composer-chip">
      <span className="tabbio-composer-chip-preview">
        {src ? <img src={src} alt="" /> : <FileImage size={13} />}
      </span>
      <span className="tabbio-composer-chip-name">{name}</span>
      <RemoveChip name={name} />
    </AttachmentPrimitive.Root>
  );
};

const FileChip: FC = () => {
  const name = useAuiState((s) => s.attachment.name);
  const contentType = useAuiState((s) => s.attachment.contentType);
  const { icon: Icon } = getFileTypeInfo(name, contentType);
  return (
    <AttachmentPrimitive.Root className="tabbio-composer-chip">
      <span className="tabbio-composer-chip-preview is-file">
        <Icon size={13} />
      </span>
      <span className="tabbio-composer-chip-name">{name}</span>
      <RemoveChip name={name} />
    </AttachmentPrimitive.Root>
  );
};

const chipComponents = {
  Image: ImageChip,
  Document: FileChip,
  File: FileChip,
};

/** The composer's attachment chips; renders nothing without attachments. */
export const ComposerAttachmentChips: FC = () => {
  const count = useAuiState((s) => s.composer.attachments.length);
  if (count === 0) return null;
  return (
    <div className="tabbio-composer-chips">
      <ComposerPrimitive.Attachments components={chipComponents} />
    </div>
  );
};
